import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { searchEntities, refUrl } from "@/lib/api";

const BADGE = {
  service: { label: "Service", cls: "bg-[#e7f0f8] text-[#16385f]" },
  company: { label: "Platform", cls: "bg-[#eaf4ec] text-[#0b6b3a]" },
  category: { label: "Category", cls: "bg-[#fdf0e3] text-[#8a4b0f]" },
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  useEffect(() => { setTerm(q); }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["search-page", q],
    queryFn: () => searchEntities(q, 50),
    enabled: q.length >= 1,
  });

  const results = data?.results || [];

  return (
    <Layout>
      <Seo
        title={q ? `Search: ${q} — CompuBrain Knowledge Base` : "Search — CompuBrain Knowledge Base"}
        description="Search across services, categories and platforms — including full page content."
        path={`/search${q ? `?q=${encodeURIComponent(q)}` : ""}`}
      />
      <div className="border-b border-[var(--line)] bg-[var(--bg-alt)]">
        <div className="mx-auto max-w-[1240px] px-4 py-5 lg:px-6">
          <Breadcrumbs items={[{ name: "Home", type: "home" }, { name: "Search" }]} />
          <h1 className="mt-3 text-3xl font-bold text-[var(--navy)]">Search the knowledge base</h1>
          <form
            onSubmit={(e) => { e.preventDefault(); setParams(term ? { q: term } : {}); }}
            className="relative mt-3 max-w-2xl"
          >
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search services, platforms, categories and content…"
              data-testid="search-page-input"
              className="w-full rounded-[4px] border border-[var(--line)] bg-white py-3 pl-10 pr-3 text-base outline-none focus:border-[var(--gov-blue)]"
            />
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-6 lg:px-6">
        {q.length < 1 ? (
          <p className="text-[var(--muted)]">Enter a term to search the knowledge base.</p>
        ) : isFetching ? (
          <p className="text-[var(--muted)]" data-testid="search-page-loading">Searching…</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-[var(--muted)]" data-testid="search-page-count">
              {results.length} result{results.length !== 1 ? "s" : ""} for “{q}”
            </p>
            {results.length === 0 ? (
              <div className="rounded-[6px] border border-[var(--line)] bg-white py-16 text-center text-[var(--muted)]" data-testid="search-page-empty">
                No results. Try a different term.
              </div>
            ) : (
              <div className="space-y-2" data-testid="search-page-results">
                {results.map((r) => {
                  const b = BADGE[r.type] || {};
                  return (
                    <Link
                      key={`${r.type}-${r.slug}`}
                      to={refUrl(r)}
                      data-testid={`search-page-result-${r.type}-${r.slug}`}
                      className="card-hover block rounded-[5px] border border-[var(--line)] bg-white p-4 no-underline"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${b.cls}`}>{b.label}</span>
                        <span className="text-lg font-bold text-[var(--navy)]">{r.name}</span>
                        {r.match === "content" && (
                          <span className="text-[11px] text-[var(--muted)]">· matched in content</span>
                        )}
                      </div>
                      {r.intro && (
                        <p className="mt-1 line-clamp-2 text-sm text-[var(--ink-soft)]">{r.intro}</p>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
